import { TASK_SORT_BY } from "constants/foreignCATask";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  useGetChatTicketsQuery,
  useGetTicketSLAMetricQuery,
} from "store/apis/chatTicket";
import { useGetTaskTemplateStatsQuery } from "store/apis/taskTemplateStats";
import { AdminAppSubscriptions } from "components/AdminAppSubscriptions/AdminAppSubscriptions";
import { AgentDashboard } from "components/AgentDashboard/AgentDashboard";
import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useFilters } from "hooks/useFilter";
import { usePageTitle } from "hooks/usePageTitle";
import { useEffect, useState } from "react";
import { useGetChatSLAMetricsQuery } from "store/apis/chat";
import { useLazyGetAllServiceProviderTaskQuery } from "store/apis/serviceTeam";
import { useChatContext, usePaginatedChannels } from "stream-chat-react";
import { getDateRange } from "utils/getDateRange";
import "static/styles/components/design/comingSoon.css";

export const AdminDashboardInfoItem = ({ header, info }) => {
  return (
    <div className="t-flex t-flex-col t-gap-y-2">
      <div className="t-text-body t-text-text-30 t-whitespace-nowrap sm:t-whitespace-normal">
        {header}
      </div>
      <div className="t-text-title-h2">
        {info !== undefined && info !== null ? info : "-"}
      </div>
    </div>
  );
};
export const ChatSLAMetricCard = () => {
  const selectionOptions = [
    { label: "Last 30 days", value: "last30days" },
    { label: "Last month", value: "lastmonth" },
    { label: "Last 3 months", value: "last3months" },
    { label: "Last 6 months", value: "last6months" },
    { label: "Last 12 months", value: "last12months" },
  ];
  const defaultDateRange = getDateRange("last30days");

  const { values, updateFilter } = useFilters({
    initialValue: {
      START_DATE: dayjs(defaultDateRange.startDate).format(DISPLAY_DATE),
      END_DATE: dayjs(defaultDateRange.endDate).format(DISPLAY_DATE),
      RANGE: "last30days",
    },
  });

  const { data, isLoading, isSuccess } = useGetChatSLAMetricsQuery(
    {
      startDate: dayjs(values.START_DATE).format(YYYY_MM_DD),
      endDate: dayjs(values.END_DATE).format(YYYY_MM_DD),
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    hours = 0,
    minutes = 0,
    seconds = 0,
  } = data?.average_chat_response_time || {};

  return (
    <>
      <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-5 t-flex t-flex-col t-w-full t-gap-5">
        <div className=" t-flex t-justify-between">
          <div className=" t-flex t-gap-3 t-items-center t-w-full t-justify-between">
            <span className="t-text-subtitle-lg t-text-text-100">Chat</span>
            <Combobox
              size="small"
              name="duration"
              onChange={(value) => {
                if (value instanceof Array) {
                  return;
                }
                const range = getDateRange(value.value);
                updateFilter("START_DATE", range.startDate);
                updateFilter("END_DATE", range.endDate);
                updateFilter("RANGE", value.value);
              }}
              menuPortalTarget={document.body}
              options={selectionOptions}
              value={selectionOptions.find(
                ({ value }) => value === values.RANGE
              )}
              isSearchable={false}
              isClearable={false}
            />
          </div>
        </div>
        <Async.Root {...{ isLoading, isSuccess, isEmpty: false }}>
          <Async.Empty>-</Async.Empty>
          <Async.Success>
            <AdminDashboardInfoItem
              header="Avg. chat response back time"
              info={`${hours} hrs ${minutes} mins ${seconds} secs`}
            />
          </Async.Success>
        </Async.Root>
      </div>
    </>
  );
};

export const TicketsSLAMetricCard = () => {
  const [duration, setDuration] = useState(3);
  const { data, isLoading, isSuccess } = useGetTicketSLAMetricQuery({
    duration: duration,
  });
  const {
    average_resolution_time,
    tickets_created_count: ticketsCreatedCount = 0,
    resolved_tickets_count: resolvedTicketsCount = 0,
  } = data || {};
  const { days = 0, hours = 0 } = average_resolution_time || {};

  const selectionOptions = [
    { label: "Last month", value: 1 },
    { label: "Last 3 months", value: 3 },
    { label: "Last 6 months", value: 6 },
    { label: "Last 12 months", value: 12 },
  ];

  return (
    <div>
      <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-5 t-flex t-flex-col t-w-full t-gap-5">
        <div className=" t-flex t-justify-between">
          <div className=" t-flex t-gap-3 t-items-center t-justify-between t-w-full">
            <span className="t-text-subtitle-lg t-text-text-100">Tickets</span>
            <Combobox
              size="small"
              name="duration"
              onChange={(value) => {
                if (value instanceof Array) {
                  return;
                }

                setDuration(value.value);
              }}
              menuPortalTarget={document.body}
              options={selectionOptions}
              value={selectionOptions.find(({ value }) => value == duration)}
              isSearchable={false}
              isClearable={false}
            />
          </div>
        </div>
        <Async.Root {...{ isLoading, isSuccess, isEmpty: false }}>
          <Async.Empty>-</Async.Empty>
          <Async.Success>
            <div className=" t-flex t-gap-[100px] sm:t-flex-wrap sm:t-gap-x-6 sm:t-gap-y-5">
              <AdminDashboardInfoItem
                header="Ticket completion time"
                info={`${days} days ${hours} hrs`}
              />
              <AdminDashboardInfoItem
                header="Tickets created"
                info={ticketsCreatedCount}
              />
              <AdminDashboardInfoItem
                header="Tickets resolved"
                info={resolvedTicketsCount}
              />
            </div>
          </Async.Success>
        </Async.Root>
      </div>
    </div>
  );
};
const WelcomeUser = () => {
  usePageTitle("Home");
  const {
    isAdmin,
    isCpa,
    isCpaInkleServiceAdmin,
    isCustomer,
    canManageInternalTickets,
  } = useRoleBasedView();
  const { data: templateDataFBAR } = useGetTaskTemplateStatsQuery(
    {
      templateId: process.env.PUBLIC_FBAR_TASK_TEMPLATE_ID,
    },
    {
      skip: !isCpaInkleServiceAdmin,
    }
  );
  const [recentlyMovedFilings, setRecentlyMovedFilings] = useState(null);
  let statList = [];
  if (templateDataFBAR) {
    statList = Object.entries(templateDataFBAR);
  }

  const { data: templateDataBooks } = useGetTaskTemplateStatsQuery(
    {
      templateId: process.env.PUBLIC_BOOKS_TASK_TEMPLATE_ID,
    },
    {
      skip: !isCpaInkleServiceAdmin,
    }
  );
  let statListBooks = [];
  if (templateDataBooks) {
    statListBooks = Object.entries(templateDataBooks);
  }

  const { data: templateData1120 } = useGetTaskTemplateStatsQuery(
    {
      templateId: process.env.PUBLIC_TASK_1120_TEMPLATE_ID,
    },
    {
      skip: !isCpaInkleServiceAdmin,
    }
  );
  let statList1120 = [];
  if (templateData1120) {
    statList1120 = Object.entries(templateData1120);
  }
  const { data: { unresolved_tickets } = { unresolved_tickets: 0 } } =
    useGetChatTicketsQuery({
      page_num: 1,
      assignee_profile_ids: null,
      resolved_status: false,
    });

  const { client } = useChatContext();
  const { channels } = usePaginatedChannels(
    client,
    {
      type: "messaging",
      needs_reply: true,
      members: { $in: [client.userID] },
    },
    {
      sort: 1,
    },
    {
      limit: 100,
    }
  );
  const [getAllServiceProviderTask] = useLazyGetAllServiceProviderTaskQuery();
  const fetchAndSaveActionItemsData = async () => {
    const recentlyMovedFilingsData = await getAllServiceProviderTask({
      page_num: 1,
      search_term: null,
      task_tag_filter_uuids: null,
      task_state_filter_uuids: null,
      task_template_filter_uuids: null,
      assignee_uuids: null,
      task_sort_by: TASK_SORT_BY.RECENTLY_MOVED_FILINGS,
      subscriptionFilter: null,
      generate_report: false,
    });

    setRecentlyMovedFilings(recentlyMovedFilingsData);
  };
  useEffect(() => {
    fetchAndSaveActionItemsData();
  }, []);

  return (
    <DashboardLayout header={<div />}>
      <div>
        {/* <p className="welcomeName">Welcome {authtoken.first_name}!</p> */}
        {canManageInternalTickets && (
          <div className="t-mb-5">
            <AgentDashboard />
          </div>
        )}
        {isAdmin && (
          <div className="t-flex t-flex-col t-gap-5">
            <AdminAppSubscriptions />
            <div className="t-grid t-w-full t-gap-x-6 t-grid-cols-3 sm:t-grid-cols-1 sm:t-gap-y-5">
              <ChatSLAMetricCard />
              <div className="t-col-span-2 sm:t-col-span-1">
                <TicketsSLAMetricCard />
              </div>
            </div>
            <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10">
              <iframe
                width="100%"
                height="400"
                frameborder="0"
                allowfullscreen
                src="https://us.posthog.com/embedded/ssNsV3Vsr9qa1SIA5Rl_nNt5kv16cw"
              ></iframe>
            </div>
            <div className="t-rounded-lg t-border t-border-solid t-border-neutral-10">
              <iframe
                width="100%"
                height="400"
                frameborder="0"
                allowfullscreen
                src="https://us.posthog.com/embedded/xzsMHkSJlcQlua-7DFDTfmVHHAdAbg"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WelcomeUser;
